import type { Patient, Task } from 'fhir/r4';
import React, { useMemo } from 'react';
import { Card, Checkbox, Icon, Label, LabelDetail } from 'semantic-ui-react';

import { TASK_ICON_COLORS } from './fixtures';
import { CardContentContainer, CardContentHeader, SelectAllWrapper } from './newTaskList.styles';
import StatusCounts from './statusCounts';
import SubList from './subList';
import { TASK_ICON_MAPS } from './subList/fixtures';
import { parseTaskDueDate } from './subList/helpers';


type NewTaskListProps = {
  currentTaskId: string | null;
  handleDisableMultiSelect: (value: boolean) => void;
  handleSelectTask: (value?: string) => void;
  handleSelectedTasks: (tasks: Task[]) => void;
  selectedTasks: Task[];
  taskList: Task[];
  fhirPatient?: Patient | undefined;
};

const NewTaskList: React.FC<NewTaskListProps> = ({
  currentTaskId,
  handleDisableMultiSelect,
  handleSelectTask,
  handleSelectedTasks,
  selectedTasks,
  taskList,
  fhirPatient,
}) => {
  const handleAllTasks = () => {
    const allTasksSelected = selectedTasks.length === Object.keys(taskList).length;
    const tempTaskList = allTasksSelected ? [] : taskList;

    handleSelectedTasks(tempTaskList);
    handleDisableMultiSelect(tempTaskList.length === 0);
  };

  const formatDate = (date: Date) => date.toISOString().slice(0, 10);

  const { dueToday, failed, overdue, triage, urgent } = useMemo(() => {
    if (!taskList?.length) {
      return {
        dueToday: 0,
        failed: 0,
        overdue: 0,
        triage: 0,
        urgent: 0,
      };
    }

    const counts = {
      dueToday: 0,
      failed: 0,
      overdue: 0,
      triage: 0,
      urgent: 0,
    };

    const todayStr = formatDate(new Date());

    // removing the completed tasks from the count
    taskList
      ?.filter((task) => task.status !== 'completed')
      .forEach((task) => {
        if (task.status === 'failed') {
          counts.failed += 1;
        }

        const periodStart = task.executionPeriod?.end || task.executionPeriod?.start;
        if (periodStart) {
          const startStr = parseTaskDueDate(periodStart).local().format('YYYY-MM-DD');

          if (startStr === todayStr) {
            counts.dueToday += 1;
          } else if (startStr < todayStr) {
            counts.overdue += 1;
          }
        }

        if (task.priority === 'asap') {
          counts.urgent += 1;
        }

        if (task.code?.coding?.[0]?.code === 'triage') {
          counts.triage += 1;
        }
      });

    return counts;
  }, [taskList]);

  const { tasksMap, numTasks } = useMemo(() => {
    const tasksMap: Record<string, Task[]> = {};
    let numTasks = 0;

    Object.keys(TASK_ICON_COLORS).forEach((category) => {
      const filteredTasks = taskList?.filter((task) => task?.code?.coding?.[0]?.code === category);

      if (filteredTasks?.length) {
        tasksMap[category] = filteredTasks;
        numTasks += filteredTasks.length;
      }
    });

    return { tasksMap, numTasks };
  }, [taskList, TASK_ICON_COLORS]);

  return (
    <>
      <StatusCounts dueToday={dueToday} failed={failed} overdue={overdue} triage={triage} urgent={urgent} />
      {numTasks > 0 &&
        Object.keys(tasksMap).map((header: string, index: number) => {
          return (
            <div key={header}>
              <Card.Content
                header={
                  <CardContentContainer>
                    <CardContentHeader>
                      <Icon size="large" name={TASK_ICON_MAPS[header]!} color={TASK_ICON_COLORS[header]!} />
                      <span>{header[0]!.toUpperCase() + header.slice(1)} </span>
                    </CardContentHeader>
                    {numTasks > 0 && index === 0 && (
                      <SelectAllWrapper>
                        <Label basic size="large">
                          Select All
                          <LabelDetail>
                            <Checkbox
                              style={{ width: '15px', height: '15px' }}
                              onClick={() => handleAllTasks()}
                              checked={selectedTasks.length === numTasks}
                            />
                          </LabelDetail>
                        </Label>
                      </SelectAllWrapper>
                    )}
                  </CardContentContainer>
                }
                description={
                  <SubList
                    // Parent's `null` (no task selected) forwarded as-is to a prop
                    // declared `string | undefined`.
                    currentTaskId={currentTaskId as string}
                    handleDisableMultiSelect={handleDisableMultiSelect}
                    handleSelectTask={handleSelectTask}
                    handleSelectTasks={handleSelectedTasks}
                    selectedTasks={selectedTasks}
                    // Asserted, not defaulted to `[]`: `tasksMap` is keyed by the
                    // headers being iterated.
                    tasks={tasksMap[header]!}
                    fhirPatient={fhirPatient}
                  />
                }
              />
            </div>
          );
        })}
      {numTasks === 0 && (
        <Card fluid>
          <Card.Content header="No Tasks" />
        </Card>
      )}
    </>
  );
};

export default NewTaskList;
